import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tasksApi, filesApi, fileUrl, teamsApi, actionCardsApi } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AssigneeAvatar } from '@/components/AssigneeAvatar';
import { toast } from 'sonner';
import {
  Send, Mic, Square, Paperclip, X, FileText, Image as ImageIcon, Loader2,
  Play, Pause, Download, TrendingUp, Check, Plus, UserPlus, Link2, ArrowUpRight,
  LogOut, User, Hash, GitMerge,
} from 'lucide-react';
import { relTime } from '@/utils/time';

function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function renderMentions(text, names) {
  if (!text) return text;
  const uniq = [...new Set(names.filter(Boolean))];
  if (!uniq.length) return text;
  const re = new RegExp(`@(${uniq.sort((a, b) => b.length - a.length).map(escapeRegex).join('|')})`, 'g');
  const out = []; let last = 0; let m; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={`m${i++}`} className="font-medium px-1 rounded" style={{ background: 'rgba(105,148,198,0.16)', color: '#3d6a9e' }}>@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
function isImg(ct) { return (ct || '').startsWith('image/'); }
function Attachment({ f }) {
  if (isImg(f.content_type)) {
    return (
      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="block mt-1.5" data-testid="task-attachment-image">
        <img src={fileUrl(f.id)} alt={f.original_filename} className="rounded-lg border border-border max-h-44 object-cover" />
      </a>
    );
  }
  return (
    <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" data-testid="task-attachment-file"
      className="inline-flex items-center gap-2 mt-1.5 px-3 py-2 rounded-lg border border-border bg-secondary/40 hover:bg-secondary transition-colors max-w-full">
      <FileText size={15} style={{ color: '#6994c6' }} className="shrink-0" />
      <span className="text-xs font-medium truncate">{f.original_filename}</span>
      <Download size={13} className="text-muted-foreground shrink-0" />
    </a>
  );
}
function VoicePlayer({ audio, transcript }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2 mt-1.5" data-testid="task-voice-note">
      <div className="flex items-center gap-2">
        <button onClick={() => { const a = ref.current; if (!a) return; playing ? a.pause() : a.play(); }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: '#6994c6' }}>
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <span className="text-xs font-medium flex items-center gap-1.5"><Mic size={12} />Voice note</span>
        <audio ref={ref} src={fileUrl(audio.id)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} className="hidden" />
      </div>
      {transcript ? <p className="text-sm text-foreground leading-relaxed italic">“{transcript}”</p>
        : <p className="text-xs text-muted-foreground italic">Transcript unavailable.</p>}
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Completed' },
];
const normStatus = (s) => (s === 'todo' ? 'not_started' : (s || 'not_started'));
const PRIPILL = (p) => p === 'high' ? { c: 'hi', l: 'High' } : p === 'low' ? { c: 'lo', l: 'Low' } : { c: 'md', l: 'Medium' };
const CONF = (c) => c === 'high' ? { w: '100%', l: 'High' } : c === 'low' ? { w: '32%', l: 'Low' } : { w: '62%', l: 'Medium' };
function fmtDue(d) {
  if (!d) return null;
  const today = new Date().toISOString().slice(0, 10);
  let label = d;
  try {
    const dt = new Date(d + 'T00:00:00');
    if (!isNaN(dt)) label = d === today ? 'Today' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { /* keep raw */ }
  return { label, cls: d < today ? 'over' : d === today ? 'today' : '' };
}

// Full task detail drawer body — renders the top action bar, scrollable detail
// (metadata grid, subtasks, conversation) and the collaborators footer. Owns the
// whole `.detail` inner. Used by the Tasks page drawer and the global task drawer.
export default function InlineTaskPanel({ task, canAssign = false, teamId = null, onChanged, onClose }) {
  const { user } = useAuth();
  const taskId = task?.id;
  const [localTask, setLocalTask] = useState(task);
  const [comments, setComments] = useState([]);
  const [canAct, setCanAct] = useState(true);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [isProgress, setIsProgress] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [members, setMembers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [addingSub, setAddingSub] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const taRef = useRef(null);

  useEffect(() => { setLocalTask(task); }, [task]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await tasksApi.listComments(taskId);
      setComments(res.data.comments || []);
      setCanAct(res.data.can_act !== false);
    } catch { setCanAct(true); }
    finally { setLoading(false); }
  }, [taskId]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await teamsApi.list();
      const seen = new Set(); const list = [];
      for (const t of (res.data.teams || [])) {
        for (const m of (t.members || [])) {
          if (m.user_id && m.user_id !== user?.id && !seen.has(m.user_id)) {
            seen.add(m.user_id); list.push({ id: m.user_id, name: m.name || m.email, email: m.email });
          }
        }
      }
      setMembers(list);
    } catch { /* noop */ }
  }, [user?.id]);

  const loadCandidates = useCallback(async () => {
    try {
      if (teamId) {
        const res = await teamsApi.list();
        const t = (res.data.teams || []).find(x => x.id === teamId);
        setCandidates((t?.members || []).map(m => ({ user_id: m.user_id, name: m.name || m.email, email: m.email, initials: (m.name || m.email || '?').slice(0, 2).toUpperCase() })));
      } else if (task?.action_card_id) {
        const res = await actionCardsApi.assignees(task.action_card_id);
        setCandidates(res.data.candidates || []);
      } else {
        const res = await teamsApi.list();
        const seen = new Set(); const list = [];
        for (const t of (res.data.teams || [])) for (const m of (t.members || [])) {
          if (m.user_id && !seen.has(m.user_id)) { seen.add(m.user_id); list.push({ user_id: m.user_id, name: m.name || m.email, email: m.email, initials: (m.name || m.email || '?').slice(0, 2).toUpperCase() }); }
        }
        setCandidates(list);
      }
    } catch { /* noop */ }
  }, [teamId, task?.action_card_id]);

  const loadSubtasks = useCallback(async () => {
    if (!taskId || localTask?.parent_task_id) { setSubtasks([]); return; }
    try { const res = await tasksApi.subtasks(taskId); setSubtasks(res.data.subtasks || []); } catch { /* noop */ }
  }, [taskId, localTask?.parent_task_id]);

  useEffect(() => {
    if (!taskId) return;
    tasksApi.recordView(taskId).catch(() => {});
    load(); loadMembers(); loadCandidates(); loadSubtasks();
  }, [taskId, load, loadMembers, loadCandidates, loadSubtasks]);

  const saveField = async (field, value) => {
    setBusy(true);
    try {
      const res = teamId ? await teamsApi.updateTask(teamId, taskId, { [field]: value })
        : await tasksApi.update(taskId, { [field]: value });
      setLocalTask(prev => ({ ...prev, ...res.data }));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const toggleComplete = () => {
    const done = normStatus(localTask?.status) === 'done';
    saveField('status', done ? 'not_started' : 'done');
  };

  const curAssigneeIds = (localTask?.assignees?.length ? localTask.assignees.map(a => a.user_id) : (localTask?.assignee_user_id ? [localTask.assignee_user_id] : [])).filter(Boolean);
  const ownerCand = candidates.find(c => c.user_id === localTask?.user_id);
  const taskOwnerName = localTask?.user_id === user?.id ? `${user?.name || 'You'} (you)` : (ownerCand?.name || localTask?.card_owner_name || 'Card owner');
  const taskOwnerInitials = ownerCand?.initials || (taskOwnerName || '?').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const assign = async (sel) => {
    setBusy(true);
    try {
      const res = teamId ? await teamsApi.assignTask(teamId, taskId, sel)
        : await tasksApi.assign(taskId, sel);
      setLocalTask(prev => ({ ...prev, ...res.data }));
      const n = Array.isArray(sel) ? sel.length : (sel ? 1 : 0);
      if (!Array.isArray(sel)) setAssignOpen(false);
      toast.success(n ? 'Assignees updated' : 'Unassigned');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign'); }
    finally { setBusy(false); }
  };

  const toggleAssignee = (uid) => {
    const next = curAssigneeIds.includes(uid) ? curAssigneeIds.filter(x => x !== uid) : [...curAssigneeIds, uid];
    assign(next);
  };

  const addCollaborator = async (uid) => {
    setBusy(true);
    try {
      const res = teamId ? await teamsApi.addTaskCollaborator(teamId, taskId, uid)
        : await tasksApi.addCollaborator(taskId, uid);
      setLocalTask(prev => ({ ...prev, ...res.data }));
      setCollabOpen(false);
      toast.success('Collaborator added');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setBusy(false); }
  };

  const removeCollaborator = async (uid) => {
    setBusy(true);
    try {
      const res = teamId ? await teamsApi.removeTaskCollaborator(teamId, taskId, uid)
        : await tasksApi.removeCollaborator(taskId, uid);
      setLocalTask(prev => ({ ...prev, ...res.data }));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
    finally { setBusy(false); }
  };

  const knownNames = [...members.map(m => m.name), ...candidates.map(c => c.name), user?.name].filter(Boolean);
  const mentionMatches = mentionQuery === null ? [] : members.filter(m => m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6);
  const onTextChange = (e) => {
    const val = e.target.value; setText(val);
    const upToCaret = val.slice(0, e.target.selectionStart);
    const m = upToCaret.match(/@(\w*)$/); setMentionQuery(m ? m[1] : null);
  };
  const pickMention = (member) => {
    const ta = taRef.current; const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, `@${member.name} `);
    setText(before + text.slice(caret));
    setSelectedMentions(prev => prev.some(s => s.id === member.id) ? prev : [...prev, member]);
    setMentionQuery(null); setTimeout(() => ta?.focus(), 0);
  };

  const send = async () => {
    if (!text.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      const attachment_file_ids = [];
      for (const f of pendingFiles) { const up = await filesApi.upload(f, { taskId }); attachment_file_ids.push(up.data.id); }
      const mention_user_ids = selectedMentions.filter(s => text.includes(`@${s.name}`)).map(s => s.id);
      await tasksApi.addComment(taskId, { text: text.trim(), type: isProgress ? 'progress' : 'text', attachment_file_ids, mention_user_ids });
      setText(''); setPendingFiles([]); setIsProgress(false); setSelectedMentions([]); setMentionQuery(null);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setUploadingVoice(true);
        try { await tasksApi.addVoiceNote(taskId, blob); await load(); onChanged?.(); toast.success('Voice note added & transcribed'); }
        catch { toast.error('Voice note failed'); } finally { setUploadingVoice(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };
  const stopRecording = () => { mediaRef.current?.stop(); setRecording(false); };

  const subAssigneeIds = (s) => (s.assignees?.length ? s.assignees.map(a => a.user_id) : (s.assignee_user_id ? [s.assignee_user_id] : [])).filter(Boolean);
  const toggleSubAssignee = async (s, uid) => {
    const cur = subAssigneeIds(s);
    const next = cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid];
    try {
      const res = await tasksApi.assign(s.id, next);
      setSubtasks(prev => prev.map(x => x.id === s.id ? { ...x, ...res.data } : x));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign subtask'); }
  };

  const addSubtask = async (e) => {
    e?.preventDefault();
    const title = newSubtask.trim(); if (!title) return;
    setAddingSub(true);
    try {
      const res = await tasksApi.create({ title, parent_task_id: taskId, action_card_id: task?.action_card_id });
      setSubtasks(prev => [...prev, res.data]); setNewSubtask(''); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add subtask'); }
    finally { setAddingSub(false); }
  };
  const toggleSubtask = async (sub) => {
    const newStatus = sub.status === 'done' ? 'not_started' : 'done';
    try { const res = await tasksApi.update(sub.id, { status: newStatus }); setSubtasks(prev => prev.map(s => s.id === sub.id ? { ...s, ...res.data } : s)); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const addTag = async (e) => {
    e?.preventDefault();
    const t = newTag.trim(); if (!t) return;
    setTagBusy(true);
    try {
      await tasksApi.addTag(taskId, t);
      setLocalTask(prev => ({ ...prev, task_tags: [...new Set([...(prev.task_tags || []), t])] }));
      setNewTag(''); onChanged?.();
    } catch { toast.error('Failed to add tag'); }
    finally { setTagBusy(false); }
  };
  const removeTag = async (tag) => {
    try {
      await tasksApi.removeTag(taskId, tag);
      setLocalTask(prev => ({ ...prev, tags: (prev.tags || []).filter(x => x !== tag), task_tags: (prev.task_tags || []).filter(x => x !== tag) }));
      onChanged?.();
    } catch { toast.error('Failed to remove tag'); }
  };
  const mergeDuplicate = async () => {
    if (!localTask?.duplicate_of?.id) return;
    setDupBusy(true);
    try { await tasksApi.mergeDuplicate(taskId, localTask.duplicate_of.id); toast.success('Merged into original'); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to merge'); }
    finally { setDupBusy(false); }
  };
  const dismissDuplicate = async () => {
    setDupBusy(true);
    try { await tasksApi.dismissDuplicate(taskId); setLocalTask(prev => ({ ...prev, possible_duplicate: false, duplicate_of: null })); toast.success('Dismissed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to dismiss'); }
    finally { setDupBusy(false); }
  };

  const collaborators = localTask?.collaborators || [];
  const takenIds = new Set([localTask?.assignee_user_id, ...collaborators.map(c => c.user_id)].filter(Boolean));
  const collabCandidates = candidates.filter(c => !takenIds.has(c.user_id));
  const subDone = subtasks.filter(s => s.status === 'done').length;
  const isDone = normStatus(localTask?.status) === 'done';
  const due = fmtDue(localTask?.due_date);
  const pr = PRIPILL(localTask?.priority);
  const conf = localTask?.confidence ? CONF(localTask.confidence) : null;
  const isCollaborator = collaborators.some(c => c.user_id === user?.id);
  const subOwner = (s) => s.assignee_name || (s.owner && s.owner !== 'Me' ? s.owner : (user?.name?.split(' ')[0] || ''));

  return (
    <>
      {/* Top action bar */}
      <div className="dt-top">
        <button className={`btn-complete${isDone ? ' is-done' : ''}`} onClick={toggleComplete} disabled={busy || !canAct} data-testid="task-mark-complete">
          <Check strokeWidth={3} />{isDone ? 'Completed' : 'Mark complete'}
        </button>
        <span className="spacer" />
        <label className="icbtn" title="Attach a file" data-testid="task-header-attach">
          <Paperclip />
          <input type="file" multiple className="hidden" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={e => { setPendingFiles(p => [...p, ...Array.from(e.target.files)]); e.target.value = ''; toast.message('File ready — add a note and Send'); }} />
        </label>
        {localTask?.action_card_id && (
          <Link to={`/action-cards/${localTask.action_card_id}`} className="icbtn" title="Open source Action Card" data-testid="task-header-card" onClick={onClose}>
            <ArrowUpRight />
          </Link>
        )}
        <button className="icbtn" title="Copy link" data-testid="task-header-link"
          onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success('Link copied'); }}>
          <Link2 />
        </button>
        <button className="icbtn" title="Close" onClick={onClose} data-testid="task-detail-close"><X /></button>
      </div>

      {/* Scrollable detail */}
      <div className="dt-scroll">
        <div className="dt-title" data-testid="task-detail-title">{localTask?.title}</div>

        {/* Possible-duplicate resolution */}
        {localTask?.possible_duplicate && (
          <div style={{ background: 'var(--butter-t)', color: 'var(--butter-tx)', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }} data-testid="task-detail-duplicate">
            <div className="lab" style={{ marginBottom: 8, color: 'var(--butter-tx)' }}>Possible duplicate{localTask.duplicate_of?.title ? ` of “${localTask.duplicate_of.title}”` : ''}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={mergeDuplicate} disabled={dupBusy} data-testid="task-merge-button"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: 'var(--blue)' }}>
                <GitMerge size={13} />Merge into original
              </button>
              <button onClick={dismissDuplicate} disabled={dupBusy} data-testid="task-dismiss-duplicate-button"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-50" style={{ border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)' }}>
                <X size={13} />Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="dt-tags" data-testid="task-detail-tags">
          {[...new Set([...(localTask?.tags || []), ...(localTask?.task_tags || [])])].map(tag => (
            <span key={tag} className="tag-chip" data-testid={`task-detail-tag-${tag}`}>
              <Hash />{tag}
              <span className="tag-x" role="button" title="Remove tag" data-testid={`task-detail-tag-remove-${tag}`} onClick={() => removeTag(tag)}>
                <X size={11} />
              </span>
            </span>
          ))}
          {canAct && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="tag-chip" style={{ paddingRight: 9, color: 'var(--faint)' }} title="Add a tag" data-testid="task-add-tag-button"><Hash /><Plus size={11} /></button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 bg-white border-border p-2.5">
                <form onSubmit={addTag} className="flex gap-1.5">
                  <input autoFocus value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="e.g. urgent" data-testid="task-add-tag-input"
                    className="flex-1 min-w-0 rounded-lg border border-border px-2 py-1 text-sm outline-none focus:border-[#5a7d52]" style={{ fontSize: '16px' }} />
                  <button type="submit" disabled={tagBusy || !newTag.trim()} data-testid="task-add-tag-submit"
                    className="inline-flex items-center justify-center px-2.5 rounded-lg text-white disabled:opacity-50" style={{ background: '#5a7d52' }}>
                    {tagBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  </button>
                </form>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="fields">
          {/* Status */}
          <div className="frow">
            <div className="fl">Status</div>
            <div className="fv">
              <select value={normStatus(localTask?.status)} onChange={e => saveField('status', e.target.value)} disabled={busy || !canAct} data-testid="task-status-select">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {/* Owner — who captured/created this; separate from who's assigned to do it */}
          <div className="frow">
            <div className="fl">Owner</div>
            <div className="fv">
              <span className="inline-flex items-center gap-2 text-[13px]" data-testid="task-owner-row" title="The person who owns the Action Card this task belongs to">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: '#8a7248' }}>{taskOwnerInitials}</span>
                <span data-testid="task-owner-name">{taskOwnerName}</span>
              </span>
            </div>
          </div>
          {/* Assignees */}
          <div className="frow">
            <div className="fl">Assignees</div>
            <div className="fv">
              {(localTask?.assignees?.length || localTask?.assignee_name)
                ? <AssigneeAvatar task={localTask} size={24} showName />
                : (canAssign
                    ? (
                      <button onClick={() => assign([user?.id])} disabled={busy} data-testid="task-assign-to-me"
                        className="inline-flex items-center gap-2 text-[13px] font-semibold px-1 py-0.5 rounded-lg transition-colors hover:bg-[var(--paper2)]" style={{ color: 'var(--blue-ink)' }}
                        title="Assign this task to yourself">
                        <span className="asg-empty" style={{ width: 24, height: 24 }}><User /></span>Assign to me
                      </button>
                    )
                    : <span className="inline-flex items-center gap-1.5 text-[color:var(--faint)]" data-testid="task-detail-unassigned"><span className="asg-empty" style={{ width: 24, height: 24 }}><User /></span>Unassigned</span>)}
              {canAssign && (
                <Popover open={assignOpen} onOpenChange={setAssignOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="task-assign-button" className="inline-flex items-center gap-1 text-[11.5px] font-bold px-1.5 py-1 rounded-lg transition-colors hover:bg-[var(--paper2)]" style={{ color: 'var(--mute)' }}>
                      <UserPlus size={12} />{curAssigneeIds.length ? 'Edit assignees' : 'Someone else'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-64 bg-white border-border p-1.5">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1.5">Assignees — tap to toggle</p>
                    <div className="max-h-56 overflow-y-auto">
                      {candidates.length === 0 ? <p className="text-xs text-muted-foreground px-2 py-2">No teammates yet</p>
                        : candidates.map(c => (
                          <button key={c.user_id} onClick={() => toggleAssignee(c.user_id)} disabled={busy} data-testid={`task-assign-option-${c.user_id}`}
                            className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-secondary flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: '#6994c6' }}>{c.initials}</span>
                            <span className="min-w-0 flex-1"><span className="block truncate">{c.name}</span>{c.email && <span className="block text-[10px] text-muted-foreground truncate">{c.email}</span>}</span>
                            {curAssigneeIds.includes(c.user_id) && <Check size={13} style={{ color: '#6994c6' }} />}
                          </button>
                        ))}
                    </div>
                    {curAssigneeIds.length > 0 && (
                      <button onClick={() => assign([])} disabled={busy} data-testid="task-assign-clear"
                        className="w-full text-left text-xs px-2 py-1.5 mt-1 rounded-lg hover:bg-secondary flex items-center gap-2" style={{ color: '#B4332E' }}>
                        <X size={12} />Unassign everyone
                      </button>
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
          {/* Due date */}
          {due && (
            <div className="frow">
              <div className="fl">Due date</div>
              <div className="fv"><span className={`r-due ${due.cls}`} data-testid="task-detail-due" style={{ fontWeight: due.cls ? 700 : 400 }}>{due.label}</span></div>
            </div>
          )}
          {/* Priority */}
          <div className="frow">
            <div className="fl">Priority</div>
            <div className="fv"><span className={`pill ${pr.c}`} data-testid="task-detail-priority">{pr.l}</span></div>
          </div>
          {/* Source */}
          {localTask?.action_card_id && localTask?.action_card_title && (
            <div className="frow">
              <div className="fl">Source</div>
              <div className="fv">
                <Link to={`/action-cards/${localTask.action_card_id}`} className="src-chip" data-testid="task-detail-source" onClick={onClose}>
                  <ArrowUpRight size={11} />{localTask.action_card_title}
                </Link>
              </div>
            </div>
          )}
          {/* Confidence */}
          {conf && (
            <div className="frow">
              <div className="fl">Confidence</div>
              <div className="fv">
                <span className="conf" data-testid="task-detail-confidence">
                  <span className="cbar"><i style={{ width: conf.w }} /></span>
                  <b>{conf.l}</b>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Subtasks */}
        {!localTask?.parent_task_id && (
          <div className="dt-block" data-testid="task-subtasks-section">
            <div className="bh"><Check />Subtasks{subtasks.length > 0 && <span style={{ letterSpacing: 0, textTransform: 'none' }}> · {subDone}/{subtasks.length}</span>}</div>
            {subtasks.map(s => (
              <div key={s.id} className={`sub-item${s.status === 'done' ? ' done' : ''}`} data-testid={`subtask-item-${s.id}`}>
                <button onClick={() => toggleSubtask(s)} data-testid={`subtask-toggle-${s.id}`}
                  className="w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0"
                  style={{ border: `2px solid ${s.status === 'done' ? 'var(--green)' : 'var(--line)'}`, background: s.status === 'done' ? 'var(--green)' : 'transparent' }}>
                  {s.status === 'done' && <Check size={10} color="white" strokeWidth={3} />}
                </button>
                <span className="tt">{s.title}</span>
                {canAct && candidates.length > 0 ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button onClick={e => e.stopPropagation()} title="Assign this subtask" data-testid={`subtask-assign-${s.id}`} className="shrink-0 inline-flex items-center">
                        {subAssigneeIds(s).length ? (
                          <span className="inline-flex items-center">
                            {(s.assignees || []).slice(0, 2).map((a, i) => (
                              <span key={a.user_id || i} className="rounded-full flex items-center justify-center font-bold text-white"
                                style={{ width: 18, height: 18, fontSize: 8, background: i === 0 ? '#6994c6' : '#5a7d52', marginLeft: i === 0 ? 0 : -6, border: '1.5px solid #fff' }}>
                                {a.initials || (a.name || '?').slice(0, 2).toUpperCase()}
                              </span>
                            ))}
                            {subAssigneeIds(s).length > 2 && <span className="rounded-full flex items-center justify-center font-bold" style={{ width: 18, height: 18, fontSize: 8, marginLeft: -6, border: '1.5px solid #fff', background: '#e7e3da', color: '#6b7482' }}>+{subAssigneeIds(s).length - 2}</span>}
                          </span>
                        ) : (
                          <span className="w-[18px] h-[18px] rounded-full border border-dashed border-border flex items-center justify-center" style={{ color: '#9aa3b0' }}><UserPlus size={10} /></span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-60 bg-white border-border p-1.5" onClick={e => e.stopPropagation()}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1">Subtask assignees — tap to toggle</p>
                      <div className="max-h-48 overflow-y-auto">
                        {candidates.map(c => (
                          <button key={c.user_id} onClick={() => toggleSubAssignee(s, c.user_id)} data-testid={`subtask-assign-option-${s.id}-${c.user_id}`}
                            className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-secondary flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: '#6994c6' }}>{c.initials}</span>
                            <span className="min-w-0 flex-1 truncate">{c.name}</span>
                            {subAssigneeIds(s).includes(c.user_id) && <Check size={12} style={{ color: '#6994c6' }} />}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <span className="who">{subOwner(s)}</span>
                )}
              </div>
            ))}
            {canAct && (
              <form onSubmit={addSubtask} className="sub-add">
                <button type="button" className="opacity-0 pointer-events-none w-0" aria-hidden />
                <input value={newSubtask} onChange={e => setNewSubtask(e.target.value)} placeholder="Add a subtask…" data-testid="subtask-input" style={{ fontSize: 16 }} />
                <button type="submit" disabled={addingSub || !newSubtask.trim()} data-testid="subtask-submit">
                  {addingSub ? <Loader2 size={13} className="animate-spin" /> : <Plus />}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Conversation */}
        <div className="dt-block" data-testid="task-conversation-section">
          <div className="bh">Conversation</div>
          <div className="convo">
            {loading ? <div className="flex justify-center py-5"><Loader2 className="animate-spin text-muted-foreground" /></div>
              : comments.length === 0 ? <p className="empty-convo">No updates yet. Start the conversation, drop a file, or record a voice note.</p>
              : comments.map(c => (
                <div key={c.id} className="msg" data-testid="task-comment">
                  <span className="av" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontWeight: 700, color: '#fff', background: c.type === 'progress' ? '#5a7d52' : '#6994c6' }}>{(c.author_name || '?').slice(0, 1).toUpperCase()}</span>
                  <div className="m-b">
                    <div className="m-h">
                      <b>{c.author_name}</b>
                      {c.type === 'progress' && <span className="inline-flex items-center gap-1" style={{ color: '#5a7d52' }}><TrendingUp size={10} />Progress</span>}
                      <span>{relTime(c.created_at)}</span>
                    </div>
                    {c.text && <p>{renderMentions(c.text, knownNames)}</p>}
                    {c.type === 'voice' && c.audio && <VoicePlayer audio={c.audio} transcript={c.transcript} />}
                    {(c.attachments || []).map(f => <Attachment key={f.id} f={f} />)}
                  </div>
                </div>
              ))}
          </div>

          {/* Composer */}
          {canAct && (
            <div className="composer">
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {pendingFiles.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg bg-white border border-border">
                      {f.type.startsWith('image/') ? <ImageIcon size={12} /> : <FileText size={12} />}
                      <span className="max-w-[140px] truncate">{f.name}</span>
                      <button onClick={() => setPendingFiles(p => p.filter((_, j) => j !== i))}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                {mentionQuery !== null && mentionMatches.length > 0 && (
                  <div className="absolute bottom-full mb-1 left-0 w-full max-w-xs bg-white border border-border rounded-xl shadow-lg overflow-hidden z-50">
                    {mentionMatches.map(m => (
                      <button key={m.id} type="button" onClick={() => pickMention(m)} className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: '#6994c6' }}>{(m.name || '?').slice(0, 1).toUpperCase()}</span>
                        <span className="min-w-0"><span className="block text-xs font-medium truncate">{m.name}</span><span className="block text-[10px] text-muted-foreground truncate">{m.email}</span></span>
                      </button>
                    ))}
                  </div>
                )}
                <textarea ref={taRef} value={text} onChange={onTextChange} rows={2}
                  placeholder={isProgress ? 'Share a progress update…' : 'Add a note or reply… use @ to mention a teammate'}
                  data-testid="task-comment-input"
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }} />
              </div>
              <div className="c-foot">
                <button onClick={() => setIsProgress(v => !v)} title="Mark as progress update" data-testid="task-toggle-progress-button"
                  className="icbtn" style={isProgress ? { background: 'rgba(90,125,82,0.14)', color: '#5a7d52' } : undefined}>
                  <TrendingUp size={16} />
                </button>
                <label className="icbtn cursor-pointer" title="Attach files" data-testid="task-attach-file-button">
                  <Paperclip size={16} />
                  <input type="file" multiple className="hidden" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    onChange={e => { setPendingFiles(p => [...p, ...Array.from(e.target.files)]); e.target.value = ''; }} />
                </label>
                {uploadingVoice ? <span className="icbtn"><Loader2 size={16} className="animate-spin" /></span>
                  : recording ? (
                    <button onClick={stopRecording} className="icbtn animate-pulse" style={{ background: 'rgba(180,51,46,0.12)', color: '#B4332E' }} title="Stop recording" data-testid="task-stop-voice-button"><Square size={16} fill="currentColor" /></button>
                  ) : (
                    <button onClick={startRecording} className="icbtn" title="Record a voice note" data-testid="task-record-voice-button"><Mic size={16} /></button>
                  )}
                <button onClick={send} disabled={sending || (!text.trim() && pendingFiles.length === 0)}
                  className={`send${(text.trim() || pendingFiles.length > 0) && !sending ? ' ready' : ''}`} data-testid="task-send-comment-button">
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send />}Send
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collaborators footer */}
      <div className="dt-foot" data-testid="task-collaborators-footer">
        <span className="lab">Collaborators</span>
        <span className="avs">
          {collaborators.map(c => (
            <span key={c.user_id} className="av" title={c.name} data-testid={`collaborator-${c.user_id}`}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontWeight: 700, color: '#fff', background: '#6994c6' }}>
              {c.initials || (c.name || '?').slice(0, 2).toUpperCase()}
            </span>
          ))}
          {collaborators.length === 0 && <span className="text-[11.5px] text-[color:var(--faint)] italic">None yet</span>}
        </span>
        {canAssign && (
          <Popover open={collabOpen} onOpenChange={setCollabOpen}>
            <PopoverTrigger asChild>
              <button className="icbtn" style={{ width: 26, height: 26 }} title="Add collaborator" data-testid="add-collaborator-button"><Plus size={15} /></button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 bg-white border-border p-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2 py-1.5">Add collaborator</p>
              <div className="max-h-56 overflow-y-auto">
                {collabCandidates.length === 0 ? <p className="text-xs text-muted-foreground px-2 py-2">No one else to add</p>
                  : collabCandidates.map(c => (
                    <button key={c.user_id} onClick={() => addCollaborator(c.user_id)} disabled={busy} data-testid={`collaborator-option-${c.user_id}`}
                      className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-secondary flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: '#6994c6' }}>{c.initials}</span>
                      <span className="min-w-0 flex-1"><span className="block truncate">{c.name}</span>{c.email && <span className="block text-[10px] text-muted-foreground truncate">{c.email}</span>}</span>
                    </button>
                  ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {isCollaborator && (
          <button className="leave" onClick={() => removeCollaborator(user.id)} disabled={busy} data-testid="leave-task-button">
            <LogOut />Leave task
          </button>
        )}
      </div>
    </>
  );
}
